// src/features/merchantConfig/components/editors/TextAreaEditor.jsx
//
// Multiline text editor for long strings (>80 chars or containing newlines).
// Same commit-on-blur semantics as StringEditor; auto-grows up to a sane
// maximum so very long values stay readable but don't dominate the panel.

import React, { useEffect, useRef, useState } from "react";
import { inputClass } from "./editorChrome";

const MIN_ROWS = 3;
const MAX_ROWS = 12;

/**
 * @param {{
 *   value: string,
 *   onCommit: (next: string) => void,
 *   ariaLabel: string,
 *   hasError?: boolean,
 *   inputId?: string,
 * }} props
 */
export default function TextAreaEditor({
  value,
  onCommit,
  ariaLabel,
  hasError,
  inputId,
}) {
  const [draft, setDraft] = useState(value ?? "");
  const ref = useRef(null);

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  // Lightweight auto-resize: count lines, clamp to [MIN_ROWS, MAX_ROWS].
  const rows = Math.min(
    Math.max(MIN_ROWS, (draft.match(/\n/g) || []).length + 1),
    MAX_ROWS
  );

  const commitIfChanged = () => {
    if (draft !== (value ?? "")) {
      onCommit(draft);
    }
  };

  return (
    <textarea
      id={inputId}
      ref={ref}
      rows={rows}
      value={draft}
      aria-label={ariaLabel}
      aria-invalid={hasError || undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commitIfChanged}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          setDraft(value ?? "");
          e.currentTarget.blur();
        }
      }}
      className={inputClass({ hasError, extra: "font-sans" })}
    />
  );
}
